import { PositionSettings } from '@mbrtargeting/metatag-config-types';
import { AdvertisementApi } from '@mbrtargeting/metatag-shared-types/ad-engine';
import { AdSize, PublisherSlotAPI, SlotEvent, SlotOptions, SystemSlotEvent, VisibilityNodeInformation } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { addCssToElement, createDivElement, debounce, DivElementParameters, generateId, generateRandomNumberString, generateUUID, isArray, isHTMLElement, isString, promisify } from '@mbrtargeting/metatag-utils';
import { Dimensions, DimensionsSource } from '../../../interfaces/interfaces.js';
import { AdslotStub } from '../../../loader/adslot-stub.js';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectCommonSettings, selectLayoutSettings, selectPositionSettings } from '../../../loader/essentials/config-selector.js';
import { triggerEvent, triggerHTMLElementEvent } from '../../../loader/essentials/events.js';
import { logger } from '../../../loader/essentials/logger.js';
import { KeyValues } from '../../../loader/key-values.js';
import { CONFIG_RESOLVER } from '../../../loader/token.js';
import { toAdSizeString } from '../../../utils/adsize-helper.js';
import { markSdg, measurePerformance } from '../../../utils/performance.js';
import { calculateLeftSpaceToViewportBorder } from '../../../utils/targeting-helper.js';
import { EffectCleanupFunction, EffectFunction, IDENTIFIER, SlotEffects } from '../../effects/index.js';
import { ADSLOT_CONTROLLER, FORMAT_CONTROLLER, GLOBAL_SETTINGS } from '../../token.js';
import { getIdsForAdSlot, setIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { constructAdUnitPath } from '../../utils/construct-adunit-path.js';
import { waitForElement$ } from '../../utils/wait-for-element.js';
import { AdLabel } from '@mbrtargeting/metatag-shared-modules';
import { FormatController } from '../format-controller/format-controller.js';
import { GlobalSettings } from '../global-settings/global-settings.js';
import { PromiseQueue } from '../rendering-state/adslot-render-state.js';
import { createAdLabel } from './adslot-adlabel.js';
import { AdslotControllerInterface, LoadIntentionOptions } from './adslot-controller-interface.js';
import { AdslotLifecycleState, LifecycleAction, LifecycleState } from './adslot-lifecycle-state.js';
import { AdslotHTMLElements, createAdslotHTMLElements } from './adslot-structure.js';

const log = logger.logGroup({ prefix: 'Adslot' });

export const isAdslotInstance = (x: unknown): x is Adslot => x instanceof Adslot;

export interface CreativeSourceElementParameters extends DivElementParameters {
    emptyOnReload?: boolean;
}

@injectionTarget()
export class Adslot extends AdslotStub implements PublisherSlotAPI, AdslotHTMLElements {

    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;
    @inject(ADSLOT_CONTROLLER) private adslotController!: AdslotControllerInterface;
    @inject(FORMAT_CONTROLLER) private formatController!: FormatController;
    @inject(GLOBAL_SETTINGS) private globalSettings!: GlobalSettings;

    public readonly slotId = generateUUID();
    public readonly divId: string;
    public readonly slotElementPromise: Promise<HTMLElement>;

    public readonly containerNode: HTMLElement;
    public readonly slotHull: HTMLElement;
    public readonly loadingSpinner: HTMLElement;
    public readonly label: AdLabel;
    public readonly adserverAnchor: HTMLElement;
    public readonly adserverContainer: HTMLElement;
    #visibilityNodes: VisibilityNodeInformation[] = [];

    public readonly activeEffects: Record<string, EffectCleanupFunction> = {};
    public advert: AdvertisementApi | null = null;
    private renderInstructions = new PromiseQueue(50)
        .onStart(() => {
            log.debug('%s: renderInstructions start', [this.slotName.toUpperCase()]);
            // "ad-is-rendered_mt_*" was defined as: "ad code has arrived at user browser, ad impression fulfilled, assets might not be fully loaded yet"
            markSdg(`ad-is-rendered_mt_${this.slotName}`);
        })
        .onEnd(() => {
            log.debug('%s: renderInstructions end', [this.slotName.toUpperCase()]);

            // "asset-is-loaded_mt_*" was defined as "all assets are loaded and the rendering process is completed"
            markSdg(`asset-is-loaded_mt_${this.slotName}`);
            const { deliveryType } = getIdsForAdSlot(this.slotName);
            if (deliveryType === 'dr') {
                markSdg(`asset-is-loaded_mt_${this.slotName}_dr`); // TICKET-34314 METATAG-548
            }

            const slotEventDetails = {
                passedObject: this,
                placement: this,
                slot: this.slotName,
                position: this.slotName,
                slotName: this.slotName,
            };
            triggerEvent(SystemSlotEvent.SDG_SLOT_RENDERED, slotEventDetails);
            triggerHTMLElementEvent(this.containerNode.parentElement, SlotEvent.SLOT_RENDERED, slotEventDetails);

            measurePerformance(`Rendering-${this.slotName.toUpperCase()}`).end();
        });
    public directRenderingFunction?: () => Promise<void>;

    public dimensions: Record<DimensionsSource, Dimensions & { priorityRequest?: boolean }> = {
        [DimensionsSource.DEFAULT]: { width: 0, height: 0 },
        [DimensionsSource.CURRENT]: { width: 0, height: 0 },
        [DimensionsSource.CSS]: { width: 0, height: 0 },
        [DimensionsSource.FORMAT]: { width: 0, height: 0 },
        [DimensionsSource.PUBLISHER]: { width: 0, height: 0 },
        [DimensionsSource.CREATIVE]: { width: 0, height: 0 },
    };
    public options: SlotOptions;
    public readonly lifecycle: AdslotLifecycleState;

    constructor(
        slotName: string,
        slotIdOrElement: string | HTMLElement,
    ) {
        super(slotName, slotIdOrElement);
        this.lifecycle = new AdslotLifecycleState(this, this.configResolver);

        const {
            reduceLayoutShift = false,
            centerAds: centerAdsGlobal = false,
            useHeightAnimations = false,
            renderAdLabels = false
        } = this.configResolver.get(selectLayoutSettings()) || {};
        const {
            showAdvertLabel = false,
            centerAds = centerAdsGlobal,
            blockLoadingSpin = false,
            lazyLoad = false,
            kvPreset = {},
            collectFreeSpace,
            reportAs,
            makeSticky = false,
            interstitial = false,
            cssContainerPreset,
            scrollInContainer = false,
            adLabelOrientation = 'horizontal',
        } = this.getPositionSettings() || {};

        if (slotIdOrElement instanceof HTMLElement) {
            this.divId = slotIdOrElement.id ||= generateId();
            this.slotElementPromise = Promise.resolve(slotIdOrElement);
        } else {
            this.divId = isString(slotIdOrElement) ? slotIdOrElement : generateId();
            this.slotElementPromise = waitForElement$(this.divId).find(e => !!e);
        }

        this.options = { // set default values based on config. Overwrite later on with publisher input from stub and/or configure() method
            reserveSpace: reduceLayoutShift,
            fixedHeight: 0,
            fixedWidth: 0,
            showAdvertLabel: renderAdLabels && showAdvertLabel,
            centerAds,
            showLoadingAnimation: !blockLoadingSpin,
            lazyLoad,
            makeSticky,
            useHeightAnimations,
            pinToBottom: false,
            hideWhenEmpty: false,
            interstitial,
            scrollInContainer,
            adLabelOrientation,
        };

        // use current SlotOptions & adjust later when changed by publisher. Label will only show when ad is delivered
        this.label = createAdLabel(this);

        // create HTML Elements and structure of the Adslot
        ({
            containerNode: this.containerNode,
            slotHull: this.slotHull,
            loadingSpinner: this.loadingSpinner,
            adserverAnchor: this.adserverAnchor,
            adserverContainer: this.adserverContainer,
        } = createAdslotHTMLElements(this));

        /**
         * workaround for most templates and prebid implementations.
         * Have some form of DIV element for the 4444 adserver ready, even before we know that is actually in use
         * Will allow prebid to get a valid HTMLElement before Google is even loaded, while providing a stable element to our GAM implementation to use.
         */
        this.getStableDivElement({
            id: `sdgGptContainer-4444-${this.slotName}`,
            emptyOnReload: true,
        });

        if (!!cssContainerPreset) addCssToElement(`#${this.divId} { ${cssContainerPreset} }`, this.containerNode);

        this.slotElementPromise.then((slotElement) => {
            slotElement.appendChild(this.containerNode);
            this.setTargeting({
                ...collectFreeSpace ? { rfree: calculateLeftSpaceToViewportBorder(this.containerNode) } : {},
            });

            log.info(`%s: slotContainer connected to parent node: %o`, [this.slotName.toUpperCase(), slotElement]);
            triggerEvent(SystemSlotEvent.SDG_SLOT_CONTAINER_AVAILABLE, { passedObject: this, placement: this, slot: this.slotName, position: this.slotName, slotName: this.slotName });

            if (/^[0-9]/.test(slotElement.id)) log.warn('%s: slotElement id=%s starting with a number - this might cause issues', [this.slotName.toUpperCase(), slotElement.id]);
        });
        this.lifecycle.onEnter(LifecycleState.DEREGISTERED, async () => {
            // disconnect our div structure from DOM, but keep child elements intact in memory
            this.containerNode.parentElement?.removeChild(this.containerNode);
        });

        // generateLocalPlacementParameters
        this.setTargeting({
            ...kvPreset,
            as: reportAs || this.slotName,
            adslot: this.slotName,
        });

        this.lifecycle.onState(LifecycleState.AVAILABLE, async () => {
            this.applyEffects();
        });
        // remove all effects on de-registration
        this.lifecycle.onAction(LifecycleAction.DEREGISTER, async () => {
            Object.values(this.activeEffects).forEach(effectCleanUpFunction => effectCleanUpFunction());
        });

        this.lifecycle.dispatch(LifecycleAction.CONSTRUCT);
    }

    /**
     * @inheritDoc
     */
    public override deconstructor() {
        this.lifecycle.dispatch(LifecycleAction.DEREGISTER);
        log.debug('%s: successfully deleted.', [this.slotName.toUpperCase()]);
    }

    public getSizes(): AdSize[] {
        return this.formatController!.getFormatTargeting(this).sizes;
    }

    public load(): PublisherSlotAPI {
        this.markLoadIntention({
            forceReload: true,
            runAuction: true,
        });
        return this;
    }

    /**
     * This is a workaround to prevent circular dependencies.
     * TODO: find a way to call adslotController directly
     */
    public markLoadIntention(options: LoadIntentionOptions): void {
        this.adslotController.markLoadIntention([this.slotName], options);
    }

    public getTargeting(): KeyValues {
        const { af, bf, sizes } = this.formatController!.getFormatTargeting(this);
        const kv = new KeyValues();
        kv.addKeyValues({
            ...this.targeting.toObject(),
            af,
            bf,
            dfpsizes: sizes.map(toAdSizeString),
        });
        return kv;
    }

    /**
     * Sets the local zone of the adslot. This will be an addition to the zone already set globally by the Publisher.
     * Used to differentiate ad slots on the same HTML page, if the publisher actually needs to do so. Only used very rarely.
     * @interface
     * @public
     * @param zone {string}
     * @returns Adslot
     */
    public setZone(zone: string): Adslot {
        if (!isString(zone)) return this;
        this.localZone = zone;
        return this;
    }

    /**
     * Sets the local pageType of the adslot. This will be an addition to the pageType already set globally by the Publisher.
     * Used to differentiate ad slots on the same HTML page, if the publisher actually needs to do so. Only used very rarely.
     * @interface
     * @public
     * @param pageType {string}
     * @returns Adslot
     */
    public setPageType(pageType: string): Adslot {
        if (isString(pageType)) {
            this.localPageType = pageType;
        }
        return this;
    }

    /**
     * gets the zone of the placement, checks if a local zone extension for this placement is used and returns it added to the global zone.
     * @interface
     * @returns {string}
     */
    public getZone(): string {
        const localZone = this.localZone || '';
        const publisherZone = this.globalSettings!.getZone();
        return (localZone) ? publisherZone + '_' + localZone : publisherZone;
    }

    /**
     * gets the pageType of the placement, checks if a local pageType extension for this placement is used and returns it added to the global pageType.
     * @interface
     * @returns {string}
     */
    public getPageType(): string {
        const localPageType = this.localPageType || '';
        const publisherPageType = this.globalSettings!.getPageType();
        return (localPageType) ? publisherPageType + '_' + localPageType : publisherPageType;
    }

    /**
     * Old interface from MetaTag1, to access the HTMLElement to which the adslot was registered.
     * This structure does not hold true anymore, but some old creative code might still use it.
     * @interface
     * @deprecated
     * @return {Element | null}
     */
    public getContainer(): Element | null {
        log.deprecated(`getContainer()`);
        //this might lead to some unexpected behaviour, since we formerly returned the parentElement of containerNode (which the slot was registered on). With new div structure tho, containerNode makes more sense globally.
        return this.containerNode;
    }

    /**
     * Will start an effect or disable it, depending on the state of "observable" argument.
     *
     * Use exported "SlotEffects" from apply-slot-effects.ts for a list of available effects.
     *
     * @param effect {EffectFunction}
     * @param observable {boolean}
     */
    public toggleEffect(effect: EffectFunction, observable: boolean): Adslot {
        const effectName = effect[IDENTIFIER] ||= effect.name.length ? effect.name : generateId();
        const effectAlreadyActive = this.activeEffects.hasOwnProperty(effectName);

        if (effectAlreadyActive && !observable) {
            this.activeEffects[effectName]();
            delete this.activeEffects[effectName];
        }
        if (!effectAlreadyActive && observable) {
            this.activeEffects[effectName] = effect(this);
        }
        return this;
    }

    /**
     * @inheritDoc
     */
    public reportAdDelivery(width: number | string, height: number | string, priorityRequest: boolean = false): Adslot {
        log.debug('%s: reportAdDelivery(%s,%s,%s) - receiving dimensions for creative', [this.slotName.toUpperCase(), width, height, priorityRequest]);

        const newHeight = +height || 0;
        const newWidth = +width || 0;
        const creativeDimensions = this.dimensions[DimensionsSource.CREATIVE]
        const widthChanged = creativeDimensions.width !== newWidth;
        const heightChanged = creativeDimensions.height < newHeight;

        const eventDetails = {
            passedObject: this,
            placement: this,
            position: this.slotName,
            slot: this.slotName,
            slotName: this.slotName,
        }

        if (heightChanged || priorityRequest) {
            const newDimensions: Dimensions = { width: +this.dimensions[DimensionsSource.CREATIVE].width, height: newHeight };
            this.dimensions[DimensionsSource.CREATIVE] = { ...newDimensions, priorityRequest };
            triggerEvent(SystemSlotEvent.SDG_SLOT_CREATIVE_AVAILABLE, eventDetails);
        }

        if (widthChanged) {
            const newDimensions: Dimensions = { width: newWidth, height: +this.dimensions[DimensionsSource.CREATIVE].height };
            this.dimensions[DimensionsSource.CREATIVE] = { ...newDimensions, priorityRequest };
            triggerEvent(SystemSlotEvent.SDG_SLOT_CREATIVE_WIDTH_AVAILABLE, eventDetails);
        }
        return this;
    }

    /**
     * @inheritDoc
     */
    public reportBlockerDelivery(shouldSlotContainerVanish?: boolean | undefined): void {
        //todo: doesnt cover all webdev use cases. What if slot should explicitly stay open? Use reportAdDelivery instead?
        this.lifecycle.dispatch(shouldSlotContainerVanish ? LifecycleAction.BLOCK_REMOVE : LifecycleAction.BLOCK_HIDE);
    }

    /**
     * @inheritDoc
     */
    public reportDefaultDelivery(): void {
        this.lifecycle.dispatch(LifecycleAction.CLEAR);
    }

    public setAd(advertisement: AdvertisementApi): Adslot {
        this.advert = advertisement;
        advertisement.abortControl.signal.addEventListener('abort', () => {
            this.advert = null;
        }, { once: true });
        return this;
    }

    public getAd(): AdvertisementApi | null {
        return this.advert;
    }

    /**
     * start effects based on slotOptions, debounce it, so we don't switch effect in short time frames.
     */
    private applyEffects = debounce(() => {
        const slotOptions = this.options;
        log.debug('%s: applyEffects with options: %o', [this.slotName.toUpperCase(), slotOptions]);
        this.toggleEffect(SlotEffects.SCROLL_IN_CONTAINER, (slotOptions.reserveSpace || slotOptions.scrollInContainer) && !slotOptions.makeSticky);
        this.toggleEffect(SlotEffects.LAZYLOAD_SLOT, slotOptions.lazyLoad);
        this.toggleEffect(SlotEffects.CENTER_ADS, slotOptions.centerAds);
        this.toggleEffect(SlotEffects.HEIGHT_ANIMATION_CHANGE, slotOptions.useHeightAnimations);
        this.toggleEffect(SlotEffects.LOADING_SPINNER, slotOptions.reserveSpace && slotOptions.showLoadingAnimation);
        this.toggleEffect(SlotEffects.CLS, slotOptions.reserveSpace && !slotOptions.makeSticky);
        this.toggleEffect(SlotEffects.STICKY_NODE2, slotOptions.makeSticky);
        this.toggleEffect(SlotEffects.PIN_TO_BOTTOM, slotOptions.pinToBottom);
        this.toggleEffect(SlotEffects.INTERSTITIAL, slotOptions.interstitial && !slotOptions.pinToBottom);
    }, 1);

    /**
     * Allows publisher to change settings for the slot. Can be called at any point during page impression.
     * For detailed documentation please see: https://stroeerdigitalgroup.atlassian.net/wiki/spaces/SDGPUBLIC/pages/51513328/English+MetaTag+Documentation#Slot.configure-(-configObject)[inlineExtension]
     *
     * @interface
     * @param options
     */
    public configure(options: Partial<SlotOptions>): PublisherSlotAPI {
        Object.entries(options).reduce((result: Record<string, unknown>, [key, value]) => {
            if (result.hasOwnProperty(key) && typeof value === typeof result[key]) {
                result[key] = value;
            }
            return result;
        }, this.options);
        // TODO alternative?
        if (!this.lifecycle.inAnyState([LifecycleState.REGISTERED])) {
            this.applyEffects();
        }
        return this;
    }

    // used by yieldlove
    public getIdsForSystem(sourceName: string) {
        // TODO evaluate sourceName?
        return getIdsForAdSlot(this.slotName);
    }

    /**
     * @deprecated
     * Import and use setIdsForAdSlot() instead. Backwards compatibility for MetaTag1 and old creative wrappers and templates
     */
    public setIdsForSystem(sourceName: string, dataValues: Record<string, string | boolean | any[]>): Adslot {
        log.deprecated('setIdsForSystem() is deprecated. Use SDG.Advertising.setIdsForAdSlot instead.');
        setIdsForAdSlot(this.slotName, { source: sourceName, values: dataValues });
        return this;
    }

    /**
     * Adds an HTMLElement to be measured by viewability tracking.
     * Elements are automatically added when rendered by ad-engine or when ad is rendered inside google iframe.
     * Use this if your creative / template code adds a new element to an already existing collection of elements.
     *
     * Information should be passed as object, containing the name and node to measure.
     * Name will be used to pass along for reporting.
     */
    public addVisibilityNode(nodeInformation: VisibilityNodeInformation) {
        const { name = 'randomName-' + generateRandomNumberString(9), node } = nodeInformation || {};
        if (isString(node) || isHTMLElement(node)) {
            this.#visibilityNodes.push({ name: name, node: node });
        } else {
            log.error('%s: input to addVisibilityNode is unexpected. Consult documentation and try again.', [this.slotName.toUpperCase()]);
        }
    }

    /**
     * Adds multiple elements to be measured by viewability tracking. Any pre-existing elements are deleted.
     * Use this if your creative / template code is not using the ad-engine and all assets to be measured are new.
     */
    public overwriteVisibilityNodes(nodeInfoArray: VisibilityNodeInformation[]) {
        const isInfoValid = ({ name, node }: VisibilityNodeInformation) => isString(name) && (isString(node) || isHTMLElement(node));
        if (isArray(nodeInfoArray) && nodeInfoArray.every(isInfoValid)) {
            this.#visibilityNodes = nodeInfoArray;
        } else {
            log.error('%s: input to overwriteVisibilityNodes is unexpected. Consult documentation and try again.', [this.slotName.toUpperCase()]);
        }
    }

    /**
     * read-only of currently collected HTMLElements to be measure by viewability vendor.
     * Measurement will start as soon as the creative has finished rendering. See "renderInstructions" on AdSlot.
     * Interface for visibility vendors.
     */
    public getVisibilityNodes(): VisibilityNodeInformation[] {
        return [...this.#visibilityNodes]; //return new array, do not allow editing the original
    }

    /**
     * Used by some templates and custom publisher scripts to get the name of the current adslot
     * @deprecated
     * @returns {string}
     */
    public getName(): string {
        log.deprecated(`getName()`);
        return this.slotName;
    }

    /**
     * Method from MetaTag1 to set an arbitrary state to the adslot. All invocations of this method should still work, but do nothing.
     * @deprecated
     * @param flagName
     * @param flagState
     */
    public setFlag(flagName: string, flagState: boolean): Adslot {
        log.deprecated(`setFlag('${flagName}')`);
        return this;
    }

    /**
     * Method from MetaTag1 to get an arbitrary state to the adslot. All invocations of this method should still work and return false, since this method was most often used to check if a specific feature should be activated.
     * Used by some templates and custom scripts from publishers.
     * @deprecated
     * @param flagName {string}
     * @returns {boolean}
     */
    public getFlag(flagName: string): boolean {
        log.deprecated(`getFlag('${flagName}')`);
        return false;
    }


    /**
     * potentially buggy.
     * Function is called by yieldlove and some templates before ad call and point to GPT container for network 4444.
     * Should another adserver (later on) deliver an ad, its adserverNode and this ID will not match.
     */
    public getAdServerNodeId(): string {
        log.deprecated('getAdServerNodeId()');
        return `sdgGptContainer-4444-${this.slotName}`;
    }

    /**
     *
     * Most delivery systems, either adserver, creative or prebid, need a definitive point to track, attach and edit HTMLElements for rendering ads.
     * AdServerNode will provide this, containerizing the rendered ad and avoid "spilling" any unwanted changes to the host website.
     * Each "creative source" will use its own node, to allow multiple active ad servers on the same page.
     *
     * Main challenge is returning the correct HTMLElement for each ad server, even before the ad call is started. Most server transport their given HTMLElements through the ad call to the finally rendered creative, demanding a stable HTMLElement at all times.
     *
     * Since this call is "currently" originating from our own prebid AND templates, assume for now that we always want to get the element associated with the 4444 GAM network
     * @deprecated
     * @interface
     */
    public getAdServerNode(): HTMLElement {
        log.deprecated('getAdServerNode()');
        return this.adserverContainer.querySelector(`#sdgGptContainer-4444-${this.slotName}`)!;

        /**
         * this might be a suitable replacement, if in the future we want to dynamically select the correct div.
         * Since we have multiple creative source, but only one source can deliver an ad, we need to identify which ad server has delivered / rendered
         *
         * this.creativeSourceElements.find(element => element.classList.contains('sdgOccupied'));
         *
         * setting the class "sdgOccupied" could be done by creative source itself
         *
         */
    }

    /**
     * Allows a creative source to create and attach itself to the adslot through an HTMLElement, which persists through reload.
     * Should a slot reload explicitly remove the div, pass {removeOnReload: true}. Consider performance costs!
     *
     * Most ad server will "clean" or "empty" its referenced HTMLElement on reload automatically.
     * You can force this by passing {emptyOnReload: true}. When removeOnReload is true, emptyOnReload will be ignored.
     *
     * @param params {CreativeSourceElementParameters}
     */
    public getStableDivElement(params: CreativeSourceElementParameters): HTMLDivElement {
        const { id, emptyOnReload = false } = params;
        if (!id) log.error('%s: calling attachCreativeSource() without valid element ID, args: %o', [this.slotName.toUpperCase(), params]);

        const foundElement = this.adserverContainer.querySelector('#' + id);
        if (foundElement instanceof HTMLDivElement) return foundElement;

        const newElement = createDivElement(params);
        newElement.setAttribute('data-stable-element', 'true');

        //removing content on reload is located in adslot-queue-state.ts -> onState(QueueState.PROCESSING
        newElement.setAttribute('data-empty-on-reload', emptyOnReload.toString());
        this.adserverContainer.appendChild(newElement);
        return newElement;
    }

    // used by yieldlove
    get localTargeting() {
        return this.getTargeting().toObject();
    }

    /**
     * Returns a list of all keyValues specific to this adSlot, which are not on the kvFilter list.
     * Used by stroeer.Core to remove unwanted key/values from their ad call.
     *
     * @returns a filtered key-values list
     */
    public getFilteredKeyValues(): Record<string, string[]> {
        const { kvFilter = [] } = this.configResolver?.get(selectCommonSettings()) || {};
        return this.getTargeting().filter(([key]) => !kvFilter.includes(key)).toObject();
    }

    /**
     * will mock some responses, that will break current metaTag1 scripts. Will allow templates and publisher to migrate to metaTag2 functionality on their on time, even if metaTag2 is already present on the site.
     *
     * @deprecated
     * @interface
     * @param statusName
     */
    public getStatus(statusName: string): boolean {
        log.deprecated(`getStatus('${statusName}') is without functionality. Please remove.`);
        if (statusName === 'isEmpty') return this.lifecycle.inAnyState([LifecycleState.AVAILABLE, LifecycleState.EMPTY]);
        if (statusName === 'loaded') return this.lifecycle.inAnyState([LifecycleState.OCCUPIED, LifecycleState.BLOCKED]);
        return true;
    }

    /**
     * will return all current config entries for the given slot from the publisher config
     */
    public getPositionSettings(): PositionSettings {
        return this.configResolver?.get(selectPositionSettings(this.slotName)) || {};
    }

    /**
     * still used in some creatives, to deactivate the adLabel. Mostly when there is no creative to show, or the creative will use its own label.
     *
     * Needs to be present in adSlotClass since creative wrapper directly references AdSlot and not AdvertisingSlotAPI. Todo Change creative wrapper as soon as metaTag2 is released fully and remove this function.
     *
     * Will hide the AdLabel temporary for this exact ad impression, to be removed on next reload.
     * Warning: Input is negated for output, take extra care when reading this function.
     *
     * @deprecated
     * @interface
     * @param show - false should hide the label, true should show it again.
     */
    public toggleAdLabel(show: boolean) {
        this.label.configure({ hide: !show });
    }

    /**
     * used by some creatives (for example vlyby, creative 138358376607) to get the adLabel and set it to display:none.
     * Force migrating to official API, by not providing legacy functionality. Editing the real adLabel would cause conflicts with our own effects. Use dummy element instead.
     * @deprecated
     */
    public getAdLabel(): HTMLElement {
        log.deprecated(`getAdLabel(). Use SDG.Advertising.toggleAdLabel(state: boolean) instead.`);
        return this.label.element;
    }

    /**
     *
     * Used by some very old templates, to remove the SlotContainer from the website. Use reportDefaultDelivery() or reportBlockerDelivery() instead
     * @deprecated
     */
    public hideContainer(): void {
        log.deprecated(`hideContainer(). Use reportDefaultDelivery() or reportBlockerDelivery() instead`);
        this.reportBlockerDelivery(false);

    }

    /**
     * used in StröerSSP PreBid bidder adapter, to map PreBid bid request to GAM ad units for additional internal logic.
     * AdUnit is returned as Array, where each string represents one adUnit level.
     * We don't want the first slash, or networkId/mcm child id. Only return actual ad units.
     */
    public getAdUnits(): string[] {
        const [_, __, ...rest] = constructAdUnitPath(this).split('/');
        return rest;
    }

    // used by yieldlove directrendering
    public getAlias(): string {
        return constructAdUnitPath(this);
    }

    // used by yieldlove directrendering
    public updatePlacementParameters(params: any) {
        log.debug('updatePlacementParameters(%o)', [params]);
    }

    public renderCreative(renderInstruction: Promise<void>): Promise<void> {
        return this.renderInstructions.add(renderInstruction);
    }

    // used by yieldlove directrendering
    public renderAd(callback: (targetDiv: HTMLDivElement) => void) {

        // will write ad to site when executed
        const renderInstructions = () => {
            // "ad-is-rendered_mt_*" was defined as: "ad code has arrived at user browser, ad impression fulfilled, assets might not be fully loaded yet"
            markSdg(`ad-is-rendered_mt_${this.slotName}_dr`);

            const targetDiv = this.getStableDivElement({
                id: `sdgDirectRenderingNode-${this.slotName}`,
                emptyOnReload: true,
            });

            //viewability vendor needs to knows which adUnit was used
            setIdsForAdSlot(this.slotName, {
                source: 'yieldlove',
                values: {
                    adUnitPath: '/DirectRender' + constructAdUnitPath(this, {
                        addNetworkId: false,
                        addMcmId: false,
                    }),
                    deliveryType: 'dr',
                }
            });

            //remove google iframe, otherwise google iframe will push direct render iframe down
            addCssToElement(`#${this.adserverContainer.id} iframe[id*=google_ads] { display: none; }`, targetDiv);

            //run creative code from prebid
            log.info('%s: DirectRendering: running renderAd callback', [this.slotName.toUpperCase()]);
            callback(targetDiv);

            //measure advert viewability, attach DV script inside stable-element DIV. Stable-element cleans childNodes up on reload automatically.
            if (targetDiv.firstChild) this.addVisibilityNode({ name: 'directRender', node: (targetDiv.firstChild as HTMLElement) });
        };

        log.info('%s: DirectRendering: setting renderAd callback', [this.slotName.toUpperCase()]);
        this.directRenderingFunction = async () => {

            await this.lifecycle.dispatch(LifecycleAction.FILL);
            this.renderCreative(promisify(renderInstructions)()); //start rendering, dont await
            this.directRenderingFunction = undefined;
        };
    }
}
